import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bookmark, ExternalLink, FolderOpen, Globe2, Layers3, Loader2 } from "lucide-react";
import type { BlogCollectionCard } from "@/lib/blog";
import { COLLECTION_DETAIL } from "@/routes";
import { collectionsService } from "@/services/collections.service";
import type { Post } from "@/services/posts.service";
import type { StashThumbnailData } from "@/components/shared/StashThumbnail";

const getPreviewImage = (post: Post | undefined): string | undefined => {
  const media = (post?.content as StashThumbnailData["content"] | undefined)?.media ?? [];
  return (
    media.find(item => item.is_thumbnail && item.type === "image")?.cdn_url
    ?? media.find(item => item.is_cover && item.type === "image")?.cdn_url
    ?? media.find(item => item.type === "image")?.cdn_url
  );
};

const PreviewImage = ({ src }: { src: string }) => {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-muted">
        <FolderOpen className="h-5 w-5 text-foreground/45" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className="h-full w-full object-cover"
    />
  );
};

const CollectionMosaic = ({
  card,
  postsMap,
  isLoading,
}: {
  card: BlogCollectionCard;
  postsMap?: Record<string, Post>;
  isLoading: boolean;
}) => {
  const images = card.previewStashIds
    .map(id => getPreviewImage(postsMap?.[id]))
    .filter((url): url is string => Boolean(url));

  return (
    <div className="relative mx-auto w-full max-w-[320px] pt-3 sm:max-w-none" aria-hidden="true">
      <div className="absolute left-4 top-0 h-4 w-24 rounded-t-md border border-b-0 border-foreground/10 bg-brand-200 dark:bg-brand-500/20" />
      <div className="relative aspect-[5/4] overflow-hidden rounded-lg rounded-tl-sm border border-foreground/10 bg-muted p-1.5">
        {images.length > 0 ? (
          <div className="grid h-full grid-cols-[1.18fr_0.82fr] grid-rows-2 gap-1.5 overflow-hidden rounded-md">
            {images.map((src, index) => (
              <div
                key={src}
                className={index === 0 ? "row-span-2 overflow-hidden" : "overflow-hidden"}
              >
                <PreviewImage src={src} />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 rounded-md bg-brand-200/60 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400">
            {isLoading ? (
              <Loader2 className="h-6 w-6 animate-spin motion-reduce:animate-none" />
            ) : (
              <FolderOpen className="h-7 w-7" />
            )}
            <span className="text-xs font-semibold">
              {isLoading ? "Loading collection preview" : "Preview unavailable"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export const BlogCollectionCardBlock = ({
  card,
  postsMap,
  arePreviewPostsLoading,
  arePreviewPostsUnavailable,
}: {
  card: BlogCollectionCard;
  postsMap?: Record<string, Post>;
  arePreviewPostsLoading: boolean;
  arePreviewPostsUnavailable: boolean;
}) => {
  const {
    data: collection,
    isLoading: isCollectionLoading,
    isError: isCollectionError,
  } = useQuery({
    queryKey: ["blog-collection-card", card.id, "public"],
    queryFn: () => collectionsService.getCollection(card.id, true),
    staleTime: 5 * 60_000,
  });

  const title = collection?.title?.trim() || card.title;
  const description = collection?.description?.trim() || card.description;
  const isLoading = isCollectionLoading || arePreviewPostsLoading;

  return (
    <aside className="not-prose overflow-hidden rounded-lg border border-foreground/10 bg-card p-5 shadow-sm sm:p-6">
      <div className="grid gap-6 sm:grid-cols-[minmax(220px,0.86fr)_minmax(0,1.14fr)] sm:items-center">
        <CollectionMosaic card={card} postsMap={postsMap} isLoading={isLoading} />

        <div className="flex min-w-0 flex-col items-start gap-4">
          <div className="flex flex-col gap-2">
            <p className="m-0 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-brand-500">
              <Globe2 className="h-3.5 w-3.5" />
              Public collection on Stasht
            </p>
            <h3 className="m-0 text-2xl font-semibold leading-tight text-foreground">{title}</h3>
            <p className="m-0 text-sm leading-relaxed text-foreground/70">{description}</p>
          </div>

          <div className="flex min-h-6 flex-wrap items-center gap-x-4 gap-y-2 text-xs font-medium text-foreground/60">
            {collection ? (
              <>
                <span className="inline-flex items-center gap-1.5">
                  <Layers3 className="h-3.5 w-3.5" />
                  {collection.item_count} {collection.item_count === 1 ? "item" : "items"}
                </span>
                {typeof collection.stash_count === "number" && (
                  <span className="inline-flex items-center gap-1.5">
                    <Bookmark className="h-3.5 w-3.5" />
                    {collection.stash_count} {collection.stash_count === 1 ? "save" : "saves"}
                  </span>
                )}
              </>
            ) : isCollectionLoading ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
                Refreshing collection details
              </span>
            ) : isCollectionError ? (
              <span className="text-destructive">Live collection details are unavailable.</span>
            ) : null}
            {arePreviewPostsUnavailable && (
              <span className="text-destructive">Preview images are unavailable.</span>
            )}
          </div>

          <Link
            to={COLLECTION_DETAIL({ collectionId: card.id })}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-foreground px-4 py-2.5 text-sm font-semibold text-background transition-colors hover:bg-brand-500 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-card"
          >
            Open collection
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </aside>
  );
};
